import { RacemapColors } from '@racemap/utilities/consts/common';
import type { StyleSpecification } from 'maplibre-gl';
import { feature } from 'topojson-client';
import type { GeometryCollection, Topology } from 'topojson-specification';
import countries110m from 'world-atlas/countries-110m.json';

const topology = countries110m as unknown as Topology<{ countries: GeometryCollection }>;
const countries = feature(topology, topology.objects.countries);

// Shapes crossing the date line jump from 180 to -180 and smear lines across the map.
// Keep each ring continuous instead; longitudes past 180 just render off the edge.
const unwrapRing = (ring: Array<Array<number>>) => {
  let prev: number | null = null;
  return ring.map(([lng, lat]) => {
    let x = lng;
    if (prev != null) {
      while (x - prev > 180) x -= 360;
      while (prev - x > 180) x += 360;
    }
    prev = x;
    return [x, lat];
  });
};

type Polygon = Array<Array<Array<number>>>;

// A polygon running past ±180 (eastern Russia, Fiji) also gets a copy on the other edge,
// since the map shows the world only once.
const withDateLineCopy = (polygon: Polygon): Array<Polygon> => {
  const xs = polygon[0].map(([x]) => x);
  const shift = Math.max(...xs) > 180 ? -360 : Math.min(...xs) < -180 ? 360 : 0;
  if (shift === 0) return [polygon];
  return [polygon, polygon.map((ring) => ring.map(([x, y]) => [x + shift, y]))];
};

countries.features = countries.features
  // Antarctica only takes up space on a map of events.
  .filter((f) => f.id !== '010')
  .map((f) => {
    const g = f.geometry;
    if (g?.type !== 'Polygon' && g?.type !== 'MultiPolygon') return f;
    const polygons = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
    const coordinates = polygons.flatMap((p) => withDateLineCopy(p.map(unwrapRing)));
    return { ...f, geometry: { type: 'MultiPolygon' as const, coordinates } };
  });

/** The page background behind the transparent canvas, which is what the map's water is. */
export const WATER_COLOR = RacemapColors.LightLightLightGray;
/** Land must read as a different surface from that water at a glance. */
export const LAND_COLOR = RacemapColors.LightGray;
/** Hover ring for events and clusters; DarkBlue, not PaleBlue (= `STATUS_COLORS.upcoming`). */
export const HOVER_STROKE = RacemapColors.DarkBlue;

/** Country outlines. File-local: nothing imports it, and an unused export fails the gate. */
const BORDER_COLOR = RacemapColors.PaleBlue;

// Country shapes and our own glyphs only: nothing external per map view. No background layer, so
// water shows the page. EventsMap's transformRequest resolves the `bundled://` glyphs URL.
export const MAP_STYLE: StyleSpecification = {
  version: 8,
  glyphs: 'bundled://{fontstack}/{range}',
  sources: { countries: { type: 'geojson', data: countries } },
  layers: [
    { id: 'land', type: 'fill', source: 'countries', paint: { 'fill-color': LAND_COLOR } },
    {
      id: 'borders',
      type: 'line',
      source: 'countries',
      paint: { 'line-color': BORDER_COLOR, 'line-width': 0.8 },
    },
  ],
};
